import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { usePageMeta } from '@/hooks/usePageMeta';
import { getStaticPageMeta } from '@/seo/meta';
import { useFormSubmit } from '@/hooks/useFormSubmit';
import { siteManifest } from '@/config/site';
import Button from '@/components/ui/Button';

export default function Contact() {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });

  const { submit, loading, success, error } = useFormSubmit('contact');
  usePageMeta(getStaticPageMeta('/contact'));

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    submit(form);
    if (!error) {
      setForm({ name: '', email: '', phone: '', message: '' });
    }
  };

  const updateField = (field: string, value: string) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  return (
    <>
      <div
        className="page-header parallaxie"
        style={{
          backgroundImage: 'linear-gradient(rgba(25,35,36,0.6), rgba(25,35,36,0.6)), url(/images/page-header-bg.jpg)',
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
        }}
      >
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="page-header-box">
                <h1>{t('contact.pageTitle')}</h1>
                <nav>
                  <ol className="breadcrumb">
                    <li className="breadcrumb-item"><a href="/">{t('common.home')}</a></li>
                    <li className="breadcrumb-item active">{t('contact.breadcrumb')}</li>
                  </ol>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="page-contact-us">
        <div className="container">
          <div className="row">
            <div className="col-lg-6">
              <div className="contact-us-image">
                <img src="/images/contact-us-image.jpg" alt="Contact" />
              </div>
            </div>

            <div className="col-lg-6">
              <div className="contact-us-form">
                <div className="section-title">
                  <h3>{t('contact.contactForm')}</h3>
                  <h2 dangerouslySetInnerHTML={{ __html: t('contact.formSubtitle') }} />
                  <p>{t('contact.formNote')}</p>
                </div>

                <form onSubmit={handleSubmit}>
                  <div className="row">
                    <div className="form-group col-md-6 mb-4">
                      <input
                        type="text"
                        className="form-control"
                        placeholder={t('form.placeholderName')}
                        value={form.name}
                        onChange={e => updateField('name', e.target.value)}
                        required
                      />
                    </div>
                    <div className="form-group col-md-6 mb-4">
                      <input
                        type="email"
                        className="form-control"
                        placeholder={t('form.placeholderEmail')}
                        value={form.email}
                        onChange={e => updateField('email', e.target.value)}
                        required
                      />
                    </div>
                    <div className="form-group col-md-12 mb-4">
                      <input
                        type="text"
                        className="form-control"
                        placeholder={t('form.placeholderPhone')}
                        value={form.phone}
                        onChange={e => updateField('phone', e.target.value)}
                      />
                    </div>
                    <div className="form-group col-md-12 mb-5">
                      <textarea
                        className="form-control"
                        rows={4}
                        placeholder={t('form.placeholderMessage')}
                        value={form.message}
                        onChange={e => updateField('message', e.target.value)}
                      />
                    </div>
                    <div className="col-md-12">
                      <Button type="submit" disabled={loading}>
                        {success ? t('form.sent') : t('form.submit')}
                      </Button>
                      {error && <p className="text-danger">{error}</p>}
                      {success && <p className="text-success">{t('form.messageSent')}</p>}
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="google-map">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
                <div className="section-title">
                  <h3>{t('contact.ourContact')}</h3>
                  <h2>{t('contact.getInTouch')}</h2>
                  <p>{t('contact.contactIntro')}</p>
                </div>
            </div>
          </div>

          <div className="row">
            <div className="col-lg-12">
              <div className="contact-info-box">
                <div className="contact-info-item">
                  <div className="icon-box"><i className="fa-solid fa-phone" aria-hidden="true"></i></div>
                  <div className="contact-info-content">
                    <h3>{t('contact.phone')}</h3>
                    <p>{siteManifest.contact.phone}</p>
                  </div>
                </div>
                <div className="contact-info-item">
                  <div className="icon-box"><i className="fa-solid fa-envelope" aria-hidden="true"></i></div>
                  <div className="contact-info-content">
                    <h3>{t('contact.email')}</h3>
                    <p>{siteManifest.contact.email}</p>
                  </div>
                </div>
                <div className="contact-info-item">
                  <div className="icon-box"><i className="fa-solid fa-location-dot" aria-hidden="true"></i></div>
                  <div className="contact-info-content">
                    <h3>{t('contact.headquarter')}</h3>
                    <p>{siteManifest.map?.office?.addressLine1}, {siteManifest.map?.office?.addressLine2}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
